import Icon from "../components/Icon";
import { useState, useRef, useEffect, Fragment } from "react";
import { fmt } from "../utils";
import { clicable } from "../components/ClicableTeclado";
import useFocoModal from "../hooks/useFocoModal";
import {
  getNotionPageBlocks, getNotionPage, updateNotionPage,
  getNotionUsers, getNotionDatabaseSchema, getNotionDatabasePages,
  getNotionViews, createNotionView, updateNotionView, deleteNotionView,
  getNotionItems, setNotionItemEnabled,
  getIdsNotionMisTareas, anadirDesdeNotion, quitarNotionDeMisTareas,
} from "../api";
import { YaEnMisTareas, NotionLogo, ConfirmarMuchas } from "../components/widgets/NotionOrigen";
import { PropertyEditor, findStatusProp, findCompletedProp, findTitleProp } from "../components/widgets/NotionPropertyEditor";
import NotionBlockEditor from "../components/widgets/NotionBlockEditor";
import NotionTable, { defaultColumns } from "../components/widgets/NotionTable";
import NotionNav, { NotionPagesManager } from "../components/widgets/NotionNav";
import NotionViewEditor from "../components/widgets/NotionViewEditor";
import {
  aplicarFiltros, ordenar, agrupar, agrupacionArrastrable, describeFiltro, SIN_VALOR,
} from "../components/widgets/notionFilters";
import MultiSelect from "../components/filtros/MultiSelect";
import BarraFiltros from "../components/filtros/BarraFiltros";
import { usePrefsFiltros } from "../components/filtros/usePrefsFiltros";

// Los mismos filtros que el gestor, con el mismo control y el mismo panel de
// configuracion: la pestaña solo cambia de donde salen los datos.
const CAMPOS = [
  { key: "estado",    label: "Estado",    icono: "flag" },
  { key: "prioridad", label: "Prioridad", icono: "exclamation-triangle-fill" },
  { key: "personas",  label: "Asignados", icono: "people-fill" },
];

// ── Preferencias de vista, por base de datos ─────────────────────────────────
// Vista (kanban/tabla) y columnas visibles se recuerdan en el navegador; son
// preferencias de presentacion, no datos, y no merecen un viaje al servidor.
const VIEW_KEY  = (dbId) => `notion.view.${dbId}`;
const COLS_KEY  = (dbId) => `notion.cols.${dbId}`;
const VISTA_KEY = (dbId) => `notion.vista.${dbId}`;   // ultima vista guardada abierta

function readPref(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}
function writePref(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* modo privado */ }
}

function relativeTime(dateStr) {
  if (!dateStr) return "nunca";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return "hace un momento";
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

// ── Icons ──────────────────────────────────────────────────────────────────────
const IconDatabase = ({ size = 15, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill={color}>
    <ellipse cx="8" cy="4" rx="6" ry="2.5"/>
    <path d="M2 4v2.5C2 7.88 4.69 9 8 9s6-1.12 6-2.5V4"/>
    <path d="M2 6.5V9C2 10.38 4.69 11.5 8 11.5S14 10.38 14 9V6.5"/>
    <path d="M2 9v2.5C2 12.88 4.69 14 8 14s6-1.12 6-2.5V9"/>
  </svg>
);
const IconSync = ({ size = 14, spinning = false, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill={color}
    style={spinning ? { animation: "spin 0.9s linear infinite" } : {}}>
    <path d="M11.534 7h3l-1.48-1.48A6 6 0 1 0 8 14a5.97 5.97 0 0 0 3.434-1.07l-.695-.88A4.98 4.98 0 0 1 8 13a5 5 0 1 1 3.534-8.534L10 6h3V3l-1.466 4z"/>
  </svg>
);
const IconChevron = ({ size = 13, down = false, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.8"
    style={{ transition: "transform 0.2s", transform: down ? "rotate(180deg)" : "rotate(0deg)" }}>
    <polyline points="4,6 8,10 12,6"/>
  </svg>
);
const IconClose = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.8">
    <line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/>
  </svg>
);
const IconExternalLink = ({ size = 12, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5">
    <path d="M7 3H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9"/>
    <path d="M10 2h4v4"/><line x1="14" y1="2" x2="8" y2="8"/>
  </svg>
);

const isEmoji = (str) => str && /\p{Emoji}/u.test(str) && str.length <= 4;

const PRIORITY_COLORS = {
  alta:   { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
  high:   { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
  media:  { bg: "#fef9c3", color: "#b45309", border: "var(--warn-line)" },
  medio:  { bg: "#fef9c3", color: "#b45309", border: "var(--warn-line)" },
  medium: { bg: "#fef9c3", color: "#b45309", border: "var(--warn-line)" },
  baja:   { bg: "#dcfce7", color: "var(--ok-text)", border: "#86efac" },
  low:    { bg: "#dcfce7", color: "var(--ok-text)", border: "#86efac" },
};
function priorityStyle(p) {
  return PRIORITY_COLORS[p?.toLowerCase()] ?? { bg: "var(--bg)", color: "var(--text3)", border: "var(--border)" };
}

// Todos en hexadecimal: personTextColor calcula la luminosidad del fondo y no sabe leer variables CSS (con var(--x) daba NaN y siempre
// texto blanco, ilegible sobre estos pasteles en tema claro). Son los valores claros de --warn-line, --ok-line y --red-line.
export const PERSON_COLORS = ["#fde68a","#bfdbfe","#bbf7d0","#fecaca","#ddd6fe","#fed7aa","#a7f3d0","#e9d5ff"];
export function personColor(name) { return PERSON_COLORS[(name.charCodeAt(0) + name.length) % PERSON_COLORS.length]; }
export function personTextColor(bg) {
  const hex = bg.replace("#","");
  const r = parseInt(hex.slice(0,2),16), g = parseInt(hex.slice(2,4),16), b = parseInt(hex.slice(4,6),16);
  return (r*0.299 + g*0.587 + b*0.114) > 150 ? "#374151" : "#fff";
}

function PersonChip({ name }) {
  const bg   = personColor(name);
  const col  = personTextColor(bg);
  const first = name.split(" ")[0];
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
      background: bg, color: col,
    }}>{first}</span>
  );
}

function DbIcon({ db, size = 16 }) {
  if (isEmoji(db?.icon)) return <span style={{ fontSize: size }}>{db.icon}</span>;
  return <IconDatabase size={size} color="var(--text3)" />;
}

// ── Selector de vista (kanban / tabla) ─────────────────────────────────────────
function ViewSwitch({ mode, onChange }) {
  const btn = (active) => ({
    padding: "5px 12px", borderRadius: 7, border: "none", cursor: "pointer",
    fontSize: 12, fontWeight: 600, fontFamily: "var(--font)",
    background: active ? "var(--surface)" : "transparent",
    color: active ? "var(--text)" : "var(--text3)",
    boxShadow: active ? "var(--shadow-sm)" : "none",
  });
  return (
    <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: 9, background: "var(--bg)", border: "1px solid var(--border)" }}>
      <button style={btn(mode === "kanban")} onClick={() => onChange("kanban")}>Tablero</button>
      <button style={btn(mode === "tabla")}  onClick={() => onChange("tabla")}>Tabla</button>
    </div>
  );
}

// ── Panel de detalle / edición (slide-in) ──────────────────────────────────────
function DetailPanel({ page, users, onClose, onItemUpdated, onItemRemoved, onNotify }) {
  const pageId = page.external_id ?? page.id;

  const [loading, setLoading]   = useState(true);
  const [live, setLive]         = useState(null);      // { values, schema, icon, url, archived }
  const [draft, setDraft]       = useState({});        // cambios pendientes por nombre de propiedad
  const [iconDraft, setIconDraft] = useState(null);    // null = sin cambios
  const [blocks, setBlocks]     = useState([]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [relOptions, setRelOptions] = useState({});    // database_id → [{id,title,icon}]

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null); setDraft({}); setIconDraft(null); setBlocks([]);

    getNotionPage(pageId)
      .then(data => {
        if (!alive) return;
        setLive(data);
        // Opciones de las propiedades tipo relation
        const dbIds = [...new Set((data.schema || []).filter(p => p.type === "relation" && p.database_id).map(p => p.database_id))];
        dbIds.forEach(id => {
          getNotionDatabasePages(id)
            .then(pages => { if (alive) setRelOptions(r => ({ ...r, [id]: pages })); })
            .catch(() => {});
        });
      })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });

    getNotionPageBlocks(pageId).then(b => { if (alive) setBlocks(b || []); }).catch(() => {});

    return () => { alive = false; };
  }, [pageId]);

  const schema   = live?.schema || [];
  const values   = live?.values || {};
  const titleDef = findTitleProp(schema);

  const valueOf = (name) => (name in draft ? draft[name] : values[name]?.value ?? null);
  const setValue = (name, v) => setDraft(d => ({ ...d, [name]: v }));

  const dirty = Object.keys(draft).length > 0 || iconDraft !== null;
  // Escape no descarta cambios sin guardar, igual que el velo de detras.
  const cajaRef = useRef(null);
  useFocoModal(cajaRef, true, () => { if (!dirty) onClose(); });

  const currentIcon = iconDraft !== null ? iconDraft : (live?.icon ?? page.icon ?? "");
  const currentTitle = titleDef ? (valueOf(titleDef.name) || "Sin título") : (page.title || "Sin título");

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const payload = {};
      if (Object.keys(draft).length) payload.properties = draft;
      if (iconDraft !== null)        payload.icon = iconDraft || null;
      const res = await updateNotionPage(pageId, payload);
      if (res.values) setLive(l => ({ ...l, values: res.values, icon: iconDraft !== null ? iconDraft : l.icon }));
      if (res.item)   onItemUpdated?.(res.item);
      setDraft({}); setIconDraft(null);
      onNotify?.("Cambios guardados en Notion");
    } catch (e) {
      setError(e.message);
      onNotify?.(e.message, "warn");
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!window.confirm("¿Archivar esta página en Notion? Podrás restaurarla desde la papelera de Notion.")) return;
    setSaving(true);
    try {
      await updateNotionPage(pageId, { archived: true });
      onItemRemoved?.(pageId);
      onNotify?.("Página archivada en Notion");
      onClose();
    } catch (e) {
      setError(e.message);
      onNotify?.(e.message, "warn");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={dirty ? undefined : onClose} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.18)" }} />

      <div ref={cajaRef} role="dialog" aria-modal="true" aria-label="Detalle de la página de Notion" style={{
        position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 301,
        width: "min(560px, 94vw)", background: "var(--surface)",
        borderLeft: "1px solid var(--border)", boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
        display: "flex", flexDirection: "column", animation: "slideIn 0.2s ease",
      }}>
        <style>{`@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>

        {/* Header */}
        <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "flex-start", gap: 10 }}>
          <input
            value={currentIcon || ""}
            onChange={e => setIconDraft(e.target.value.slice(0, 4))}
            title="Icono (emoji)"
            placeholder="🙂"
            style={{
              width: 36, height: 36, borderRadius: 8, background: "var(--bg)", border: "1px solid var(--border)",
              textAlign: "center", fontSize: 18, color: "var(--text)", flexShrink: 0, fontFamily: "var(--font)",
            }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {titleDef ? (
              <textarea aria-label={titleDef.name} rows={1} value={valueOf(titleDef.name) ?? ""}
                onChange={e => setValue(titleDef.name, e.target.value)}
                onInput={e => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
                style={{
                  width: "100%", border: "1px solid transparent", background: "transparent", resize: "none",
                  fontWeight: 800, fontSize: 15.5, lineHeight: 1.35, color: "var(--text)",
                  fontFamily: "var(--font)", padding: "4px 6px", borderRadius: 7, overflow: "hidden",
                }}
                onFocus={e => { e.target.style.border = "1px solid var(--border)"; e.target.style.background = "var(--surface2)"; }}
                onBlur={e => { e.target.style.border = "1px solid transparent"; e.target.style.background = "transparent"; }} />
            ) : (
              <div style={{ fontWeight: 800, fontSize: 15, color: "var(--text)", lineHeight: 1.3, padding: "4px 6px" }}>{currentTitle}</div>
            )}
          </div>
          <button aria-label="Cerrar el detalle" onClick={onClose} style={{minWidth:24,minHeight:24,alignItems:"center",justifyContent:"center",display:"inline-flex", background: "transparent", border: "none", cursor: "pointer", padding: 4, color: "var(--text3)", flexShrink: 0 }}>
            <IconClose size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 22 }}>
          {loading && <div style={{ fontSize: 13, color: "var(--text3)" }}>Cargando desde Notion…</div>}

          {error && (
            <div style={{ fontSize: 12.5, color: "#dc2626", background: "var(--red-bg)", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 11px" }}>
              {error}
            </div>
          )}

          {!loading && live && (
            <>
              {/* Propiedades */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Propiedades</div>
                {schema.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: "var(--text3)" }}>
                    Esta página no pertenece a una base de datos, así que no tiene propiedades editables.
                  </div>
                ) : (
                  <div className="prop-grid" style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "10px 12px", alignItems: "start" }}>
                    {schema.filter(def => def.type !== "title").map(def => (
                      <Fragment key={def.name}>
                        <span title={def.type} style={{ fontSize: 12, color: "var(--text3)", fontWeight: 600, paddingTop: 8, wordBreak: "break-word" }}>
                          {def.name}
                        </span>
                        <PropertyEditor
                          def={def}
                          value={valueOf(def.name)}
                          onChange={v => setValue(def.name, v)}
                          users={users}
                          relationOptions={relOptions[def.database_id] || []} />
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>

              {/* Contenido de la página */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Contenido</div>
                <NotionBlockEditor pageId={pageId} blocks={blocks} setBlocks={setBlocks}
                  disabled={saving} onError={msg => { setError(msg); onNotify?.(msg, "warn"); }} />
              </div>

              {/* Zona de riesgo */}
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: 14 }}>
                <button onClick={archive} disabled={saving} style={{
                  fontSize: 12, fontWeight: 600, color: "#dc2626", background: "transparent",
                  border: "1px solid #fca5a5", borderRadius: 7, padding: "6px 12px", cursor: "pointer",
                }}>Archivar en Notion</button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
          {(page.url || live?.url) && (
            <a href={live?.url || page.url} target="_blank" rel="noopener noreferrer" style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px",
              borderRadius: 8, textDecoration: "none", background: "#000", color: "#fff", fontSize: 12.5, fontWeight: 600,
            }}>
              <span style={{ fontFamily: "Georgia, serif", fontWeight: 900, fontSize: 14 }}>N</span>
              Abrir
              <IconExternalLink size={11} color="#fff" />
            </a>
          )}
          <div style={{ flex: 1 }} />
          {dirty && (
            <button onClick={() => { setDraft({}); setIconDraft(null); }} disabled={saving} style={{
              fontSize: 12.5, fontWeight: 600, padding: "8px 14px", borderRadius: 8,
              border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text2)", cursor: "pointer",
            }}>Descartar</button>
          )}
          <button onClick={save} disabled={!dirty || saving} style={{
            fontSize: 12.5, fontWeight: 700, padding: "8px 18px", borderRadius: 8, border: "none",
            background: dirty ? "var(--accent)" : "var(--bg)", color: dirty ? "var(--on-accent)" : "var(--text3)",
            cursor: dirty && !saving ? "pointer" : "default",
          }}>{saving ? "Guardando…" : dirty ? "Guardar cambios" : "Sin cambios"}</button>
        </div>
      </div>
    </>
  );
}

// ── Tarjeta del kanban ─────────────────────────────────────────────────────────
// `mover` ({ columna, opciones, onMover }) es la alternativa al arrastre (WCAG
// 2.5.7): un desplegable para cambiar la tarjeta de columna sin ratón.
export function KanbanCard({ page, onClick, onToggleCompleted, draggable, enlace = null, seleccion = null, mover = null }) {
  const people   = page.people ? page.people.split(",").map(s => s.trim()).filter(Boolean) : [];
  const pid      = page.external_id ?? page.id;
  const yaEsta   = seleccion?.ya.has(pid);
  const elegida  = seleccion?.ids.has(pid);
  const pStyle   = priorityStyle(page.priority);
  const hasEmoji = isEmoji(page.icon);

  return (
    <div
      {...clicable(onClick, { role: null })}
      role="group"
      aria-label={page.title || "Sin título"}
      draggable={draggable}
      onDragStart={e => { e.dataTransfer.setData("text/plain", page.external_id ?? page.id); e.dataTransfer.effectAllowed = "move"; }}
      style={{
        background: elegida ? "var(--accent-bg)" : "var(--surface)", borderRadius: 10,
        border: "1px solid " + (elegida ? "var(--accent)" : "var(--border)"), boxShadow: "var(--shadow-sm)",
        padding: "12px 14px", cursor: draggable ? "grab" : "pointer",
        transition: "box-shadow 0.15s, transform 0.1s",
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "var(--shadow-sm)"; e.currentTarget.style.transform = "none"; }}
    >
      {/* Enlazada en ALM (solo en modo enlace) */}
      {enlace && (
        <label onClick={e => e.stopPropagation()}
          style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 9, fontSize: 11, fontWeight: 600, color: page.enabled === false ? "var(--text3)" : "var(--accent)", cursor: "pointer" }}>
          <input type="checkbox" checked={page.enabled !== false} disabled={enlace.busy?.includes(page.external_id ?? page.id)}
            onChange={() => enlace.onToggle(page)}
            style={{ accentColor: "var(--accent)", width: 13, height: 13, flexShrink: 0 }} />
          {page.enabled === false ? "Fuera de ALM" : "En ALM"}
        </label>
      )}

      {/* Modo «Seleccionar»: casilla, o la marca si ya esta en Mis tareas */}
      {seleccion && (
        <div onClick={e => e.stopPropagation()} style={{ marginBottom: 9 }}>
          {yaEsta
            ? <YaEnMisTareas page={page} onQuitar={seleccion.onQuitar} />
            : (
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, minHeight: 28, fontSize: 11.5, fontWeight: 600, color: "var(--accent)", cursor: "pointer" }}>
                <input type="checkbox" checked={!!elegida} onChange={() => seleccion.onToggle(page)}
                  aria-label={`Elegir «${page.title || "Sin título"}»`}
                  style={{ accentColor: "var(--accent)", width: 18, height: 18, flexShrink: 0 }} />
                Elegir
              </label>
            )}
        </div>
      )}

      {/* Alternativa de teclado al arrastre entre columnas */}
      {mover && mover.opciones.length > 0 && (
        <select value={mover.columna} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}
          onChange={e => mover.onMover(page, e.target.value)}
          aria-label={`Mover «${page.title || "Sin título"}» a otra columna`}
          style={{ marginBottom: 9, maxWidth: "100%", fontSize: 11.5, padding: "3px 6px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text2)", fontFamily: "var(--font)" }}>
          {[mover.columna, ...mover.opciones.filter(o => o !== mover.columna)].map(o => (
            <option key={o} value={o}>{o === mover.columna ? `Columna: ${o}` : `Mover a ${o}`}</option>
          ))}
        </select>
      )}

      {/* Título */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
        {hasEmoji && <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{page.icon}</span>}
        <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: page.completed ? "var(--text3)" : "var(--text)", textDecoration: page.completed ? "line-through" : "none" }}>
          {page.title || "Sin título"}
        </span>
      </div>

      {/* Completada (checkbox de Notion, editable) */}
      {page.completed != null && (
        <label
          onClick={e => e.stopPropagation()}
          style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, fontSize: 11.5, fontWeight: 600, color: page.completed ? "var(--ok-text)" : "var(--text3)", cursor: onToggleCompleted ? "pointer" : "default" }}>
          <input type="checkbox" checked={page.completed} disabled={!onToggleCompleted}
            onChange={e => onToggleCompleted?.(page, e.target.checked)}
            style={{ accentColor: "var(--ok-solid)", width: 13, height: 13, flexShrink: 0 }} />
          Completada
        </label>
      )}

      {/* Personas */}
      {people.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
          {people.map((name, i) => <PersonChip key={i} name={name} />)}
        </div>
      )}

      {/* Prioridad */}
      {page.priority && (
        <div>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
            background: pStyle.bg, color: pStyle.color, border: `1px solid ${pStyle.border}`,
          }}>{page.priority}</span>
        </div>
      )}

      {page.end_date && (
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--text3)" }}>
          📅 {fmt(page.end_date)}
        </div>
      )}
    </div>
  );
}

// ── Colores de columna por estado ──────────────────────────────────────────────
const STATUS_STYLES = {
  "sin empezar":    { dot: "#9ca3af" },
  "pendiente":      { dot: "#f59e0b" },
  "en progreso":    { dot: "#3b82f6" },
  "en proceso":     { dot: "#3b82f6" },
  "pendiente subir":{ dot: "#8b5cf6" },
  "listo":          { dot: "#10b981" },
  "terminado":      { dot: "#10b981" },
  "completado":     { dot: "#10b981" },
};
function statusDot(s) {
  return STATUS_STYLES[s?.toLowerCase()] ?? { dot: "var(--text3)" };
}


// ── Componente principal ───────────────────────────────────────────────────────
export default function NotionProjectsPage({
  notionItems = [], integration, token, onSync, onConnect,
  onItemUpdated, onItemRemoved, onItemsChanged, showToast,
  seleccionInicial = false, onSeleccionVista,
}) {
  const [syncing, setSyncing]   = useState(false);
  const [nodoId, setNodoId]     = useState(null);      // database seleccionada en el árbol
  const [selectedPage, setSelectedPage] = useState(null);
  const [schema, setSchema]     = useState([]);
  const [users, setUsers]       = useState([]);
  const [dragOver, setDragOver] = useState(null);
  const [busyIds, setBusyIds]   = useState([]);        // páginas con un PATCH en vuelo

  const [vistas, setVistas]     = useState([]);        // vistas guardadas de la db activa
  const [vistaId, setVistaId]   = useState(null);      // null = vista rápida (sin guardar)
  const [editando, setEditando] = useState(null);      // vista en edición, o {} para una nueva
  const [gestionando, setGestionando] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  // Modo enlace: la vista se convierte en el sitio donde decides que paginas
  // entran en ALM. Filtras con la vista y marcas las que valen, en vez de ir a
  // buscarlas a una lista aparte sin contexto.
  const [modoEnlace, setModoEnlace] = useState(false);
  // Estado, Prioridad y Asignados van plegados tras «Filtros (n)»; se abren solos si hay alguno puesto.
  const [verFiltros, setVerFiltros] = useState(false);
  const [todasDb, setTodasDb]       = useState(null);   // incluye las desconectadas
  const [enlaceBusy, setEnlaceBusy] = useState([]);

  // Modo «Seleccionar»: la persona elige que paginas pasan a «Mis tareas» (Notion es opt-in).
  // Es excluyente con «Enlazar»: uno decide que entra en ALM, el otro que entra en tus tareas.
  const [modoSel, setModoSel]     = useState(false);
  const [elegidas, setElegidas]   = useState(() => new Set());
  const [yaEstan, setYaEstan]     = useState(() => new Set());
  const [confirmando, setConfirmando] = useState(false);
  const [anadiendo, setAnadiendo] = useState(false);
  const [anuncio, setAnuncio]     = useState("");

  // Preferencias de la vista rápida, que es la de siempre
  const [viewMode, setViewMode]       = useState("kanban");
  const [visibleCols, setVisibleCols] = useState([]);
  const [filterStatus, setFilterStatus]     = useState([]);
  const [filterPriority, setFilterPriority] = useState([]);
  const [filterPeople, setFilterPeople]     = useState([]);

  const databases  = notionItems.filter(i => i.item_type === "database");
  const activeDbId = databases.some(d => d.external_id === nodoId) ? nodoId : (databases[0]?.external_id ?? null);
  const activeDb   = databases.find(d => d.external_id === activeDbId);

  // Esquema de la database activa. La sincronización ya lo dejó guardado, así que
  // se pinta al instante; luego se refresca contra Notion por si cambió allí.
  useEffect(() => {
    if (!integration || !activeDbId) { setSchema([]); return; }
    let alive = true;
    setSchema(databases.find(d => d.external_id === activeDbId)?.db_schema || []);
    getNotionDatabaseSchema(activeDbId)
      .then(d => { if (alive && d.properties?.length) setSchema(d.properties); })
      .catch(() => {});
    return () => { alive = false; };
    // `databases` se recalcula en cada render; el efecto depende de la id, no del array.
  }, [activeDbId, integration]);   // eslint-disable-line

  // Vistas guardadas de la database activa
  useEffect(() => {
    if (!integration || !activeDbId) { setVistas([]); return; }
    let alive = true;
    getNotionViews(activeDbId)
      .then(vs => {
        if (!alive) return;
        const lista = Array.isArray(vs) ? vs : [];
        setVistas(lista);
        // En cuanto hay una vista configurada, ella manda: la vista general es
        // el punto de partida de una database sin configurar, no una pestana
        // mas compitiendo con las tuyas.
        const ultima = readPref(VISTA_KEY(activeDbId), null);
        setVistaId(lista.some(v => v.id === ultima) ? ultima : (lista[0]?.id ?? null));
      })
      .catch(() => { if (alive) { setVistas([]); setVistaId(null); } });
    return () => { alive = false; };
  }, [activeDbId, integration]);

  // Preferencias locales de la vista rápida
  useEffect(() => {
    if (!activeDbId) return;
    setViewMode(readPref(VIEW_KEY(activeDbId), "kanban"));
    setFilterStatus([]); setFilterPriority([]); setFilterPeople([]); setBusqueda("");
  }, [activeDbId]);

  useEffect(() => {
    if (!activeDbId || !schema.length) return;
    const saved = readPref(COLS_KEY(activeDbId), null);
    const valid = Array.isArray(saved) ? saved.filter(n => schema.some(p => p.name === n)) : null;
    setVisibleCols(valid && valid.length ? valid : defaultColumns(schema));
  }, [activeDbId, schema]);

  useEffect(() => {
    if (!integration) return;
    let alive = true;
    getNotionUsers().then(u => { if (alive) setUsers(u || []); }).catch(() => {});
    return () => { alive = false; };
  }, [integration]);

  // Las desconectadas no vienen en la lista normal, asi que en modo enlace hay
  // que pedirlas aparte: si no, no habria forma de volver a traer una.
  useEffect(() => {
    if (!modoEnlace) { setTodasDb(null); return; }
    let alive = true;
    getNotionItems({ todo: true })
      .then(d => { if (alive) setTodasDb(Array.isArray(d) ? d : []); })
      .catch(e => { if (alive) { setModoEnlace(false); showToast?.(e.message || "No se pudieron cargar las páginas", "error"); } });
    return () => { alive = false; };
  }, [modoEnlace, showToast]);

  const notify = (msg, kind) => showToast?.(msg, kind);

  // «Traer tareas de Notion» desde el estado vacio de Mis tareas entra con la seleccion ya activa
  useEffect(() => {
    if (seleccionInicial) { setModoSel(true); onSeleccionVista?.(); }
  }, []);   // eslint-disable-line

  // Al entrar en el modo se piden los ids que ya estan en Mis tareas (para marcar «Ya en Mis tareas»)
  useEffect(() => {
    if (!modoSel) return;
    let alive = true;
    getIdsNotionMisTareas()
      .then(d => { if (alive) setYaEstan(new Set(d?.ids || [])); })
      .catch(() => { if (alive) { setModoSel(false); showToast?.("«Mis tareas» aún no está disponible para ti", "warn"); } });
    return () => { alive = false; };
  }, [modoSel, showToast]);

  const cambiarModoSel = () => {
    setModoEnlace(false); setElegidas(new Set()); setConfirmando(false); setAnuncio("");
    setModoSel(m => !m);
  };
  const toggleElegida = (page) => {
    const id = page.external_id ?? page.id;
    setAnuncio("");
    setElegidas(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  // Responde el servidor con { añadidas, ya_estaban, total, omitidas, truncado }
  const textoAnadidas = (r) => {
    const n = r?.["añadidas"] ?? 0, ya = r?.ya_estaban ?? 0;
    let t = `${n} ${n === 1 ? "tarea añadida" : "tareas añadidas"} a Mis tareas`;
    if (ya) t += ` · ${ya} ya estaban`;
    if (r?.truncado) t += " · se ha llegado al máximo por vez, repite para traer el resto";
    return t;
  };
  const anadir = async (cuerpo) => {
    setAnadiendo(true);
    try {
      const r = await anadirDesdeNotion(cuerpo);
      const d = await getIdsNotionMisTareas().catch(() => null);
      if (d?.ids) setYaEstan(new Set(d.ids));
      setElegidas(new Set()); setConfirmando(false);
      setAnuncio(textoAnadidas(r));
    } catch (e) {
      setConfirmando(false);
      notify(e.message || "No se han podido añadir", "error");
    } finally {
      setAnadiendo(false);
    }
  };
  const quitarDeMisTareas = async (page) => {
    const id = page.external_id ?? page.id;
    try {
      await quitarNotionDeMisTareas(id);
      setYaEstan(s => { const n = new Set(s); n.delete(id); return n; });
      setAnuncio("Quitada de Mis tareas");
    } catch (e) {
      notify(e.message || "No se ha podido quitar", "error");
    }
  };

  const changeView  = (mode) => { setViewMode(mode); if (activeDbId) writePref(VIEW_KEY(activeDbId), mode); };
  const changeCols  = (cols) => { setVisibleCols(cols); if (activeDbId) writePref(COLS_KEY(activeDbId), cols); };
  const changeVista = (id)   => { setVistaId(id); if (activeDbId) writePref(VISTA_KEY(activeDbId), id); };

  // Escribe una propiedad de una página y refleja el resultado en el tablero
  const patchProperty = async (page, propName, value) => {
    const id = page.external_id ?? page.id;
    setBusyIds(b => [...b, id]);
    try {
      const res = await updateNotionPage(id, { properties: { [propName]: value } });
      if (res.item) onItemUpdated?.(res.item);
    } catch (e) {
      notify(e.message || "Notion rechazó el cambio", "warn");
    } finally {
      setBusyIds(b => b.filter(x => x !== id));
    }
  };

  // Conecta o desconecta una pagina desde la propia vista
  const toggleEnlace = async (page) => {
    const id = page.external_id ?? page.id;
    const enabled = page.enabled === false;      // si estaba fuera, entra
    setEnlaceBusy(b => [...b, id]);
    try {
      await setNotionItemEnabled(id, enabled);
      setTodasDb(list => (list || []).map(x => (x.external_id === id ? { ...x, enabled } : x)));
      onItemsChanged?.();
    } catch (e) {
      notify(e.message || "No se pudo cambiar", "error");
    } finally {
      setEnlaceBusy(b => b.filter(x => x !== id));
    }
  };

  // ── Guardar / borrar vistas ─────────────────────────────────────────────────
  const guardarVista = async ({ name, config }) => {
    try {
      if (editando?.id) {
        const v = await updateNotionView(editando.id, { name, config });
        setVistas(list => list.map(x => (x.id === v.id ? v : x)));
        changeVista(v.id);
        notify("Vista actualizada");
      } else {
        const v = await createNotionView({ database_id: activeDbId, name, config });
        setVistas(list => [...list, v]);
        changeVista(v.id);
        notify("Vista creada");
      }
      setEditando(null);
    } catch (e) {
      notify(e.message || "No se pudo guardar la vista", "error");
    }
  };

  // Sin boton "+ Vista", tener varias pantallas de una misma base de datos pasa
  // por aqui: se configura sobre una y se guarda aparte.
  const guardarComoNueva = async ({ name, config }) => {
    try {
      const v = await createNotionView({ database_id: activeDbId, name, config });
      setVistas(list => [...list, v]);
      changeVista(v.id);
      setEditando(null);
      notify("Vista creada");
    } catch (e) {
      notify(e.message || "No se pudo crear la vista", "error");
    }
  };

  const borrarVista = async () => {
    if (!editando?.id) return;
    if (!window.confirm(`¿Eliminar la vista «${editando.name}»?`)) return;
    try {
      await deleteNotionView(editando.id);
      const resto = vistas.filter(v => v.id !== editando.id);
      setVistas(resto);
      changeVista(resto[0]?.id ?? null);
      setEditando(null);
      notify("Vista eliminada");
    } catch (e) {
      notify(e.message || "No se pudo eliminar", "error");
    }
  };

  if (!integration) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 340 }}>
        <div style={{ textAlign: "center", maxWidth: 360 }}>
          <div style={{ width: 56, height: 56, borderRadius: 10, background: "#000", color: "#fff", fontSize: 30, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px", fontFamily: "Georgia, serif" }}>N</div>
          <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8, color: "var(--text)" }}>Conecta tu cuenta de Notion</div>
          <div style={{ color: "var(--text3)", fontSize: 13.5, lineHeight: 1.6, marginBottom: 22 }}>Sincroniza tus proyectos y páginas de Notion para visualizarlos y editarlos aquí.</div>
          <button className="btn" onClick={onConnect} style={{ background: "#000", color: "#fff", border: "none", borderRadius: 8, padding: "9px 22px", fontWeight: 600, fontSize: 13.5, cursor: "pointer" }}>Conectar Notion</button>
        </div>
      </div>
    );
  }

  const handleSync = async () => {
    setSyncing(true);
    try { await onSync(); } catch (_) { } finally { setSyncing(false); }
  };

  // ── Qué se pinta ────────────────────────────────────────────────────────────
  const vista  = vistas.find(v => v.id === vistaId) || null;
  const config = vista?.config || null;
  // La vista guardada manda; sin ella, el interruptor local de siempre.
  const tipo   = config ? (config.tipo || "tablero") : (viewMode === "tabla" ? "tabla" : "tablero");

  const fuente   = modoEnlace && todasDb ? todasDb : notionItems;
  const allPages = fuente.filter(i => i.item_type !== "database" && i.parent_external_id === activeDbId);

  const statusProp    = findStatusProp(schema);
  const completedProp = findCompletedProp(schema);

  // La propiedad que da las columnas del tablero: la de la vista, o el estado
  // adivinado de siempre cuando se está en la vista rápida.
  const groupDef = config
    ? (config.agruparPor ? schema.find(p => p.name === config.agruparPor) || null : null)
    : statusProp;

  const statusOptions   = statusProp?.options?.length
    ? statusProp.options.map(o => o.name)
    : [...new Set(allPages.map(p => p.status).filter(Boolean))];
  const priorityOptions = [...new Set(allPages.map(p => p.priority).filter(Boolean))];
  const peopleOptions   = [...new Set(allPages.flatMap(p => p.people ? p.people.split(",").map(s => s.trim()).filter(Boolean) : []))].sort();

  // Al configurar desde la vista general se arranca de lo que ya estás viendo:
  // el tipo de vista elegido, sus columnas y la agrupación por estado.
  const semillaVista = () => ({
    tipo:       viewMode === "tabla" ? "tabla" : "tablero",
    agruparPor: statusProp?.name || null,
    columnas:   visibleCols,
    filtros:    [],
    modo:       "todos",
    ordenProp:  null,
    ordenDir:   "asc",
  });

  // Filtros rápidos de la barra + los de la vista guardada, todos a la vez
  let filtered = allPages.filter(p => {
    if (filterStatus.length   && !filterStatus.includes(p.status))     return false;
    if (filterPriority.length && !filterPriority.includes(p.priority)) return false;
    if (filterPeople.length) {
      const pp = p.people ? p.people.split(",").map(s => s.trim()) : [];
      if (!filterPeople.some(fp => pp.includes(fp))) return false;
    }
    if (busqueda.trim() && !(p.title || "").toLowerCase().includes(busqueda.trim().toLowerCase())) return false;
    return true;
  });
  if (config?.filtros?.length) filtered = aplicarFiltros(filtered, config.filtros, config.modo);
  if (config?.ordenProp)       filtered = ordenar(filtered, config.ordenProp, config.ordenDir);

  // Modo «Seleccionar»: lo elegido, lo que ya esta y las acciones de la barra
  const seleccion = modoSel
    ? { ids: elegidas, ya: yaEstan, onToggle: toggleElegida, onQuitar: quitarDeMisTareas }
    : null;
  const idDe = p => p.external_id ?? p.id;
  const visiblesSinTraer = filtered.filter(p => !yaEstan.has(idDe(p)));
  const nFiltro = filtered.length;
  // Los criterios van al servidor, que es quien los evalua; N solo sirve para avisar y confirmar
  const anadirFiltro = () => anadir({
    todos: true,
    filtro: {
      base: activeDbId, estados: filterStatus, prioridades: filterPriority, personas: filterPeople,
      busqueda: busqueda.trim(), filtros: config?.filtros || [], modo: config?.modo || "todos",
    },
  });

  const columnas    = groupDef ? agrupar(filtered, groupDef) : [];
  const arrastrable = agrupacionArrastrable(groupDef);

  const canDropOn = (key) => {
    if (!arrastrable) return false;
    if (key === SIN_VALOR) return groupDef.type === "select";   // "status" no admite vacío
    return true;
  };

  const moverA = (page, key) => {
    if (!page || !canDropOn(key)) return;
    const actual = page.properties?.[groupDef.name]?.value ?? null;
    if (actual === (key === SIN_VALOR ? null : key)) return;
    patchProperty(page, groupDef.name, key === SIN_VALOR ? null : key);
  };

  const handleDrop = (e, key) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    moverA(allPages.find(p => (p.external_id ?? p.id) === id), key);
  };

  const toggleCompleted = completedProp
    ? (page, checked) => patchProperty(page, completedProp.name, checked)
    : null;

  const columnasTabla = config
    ? (config.columnas?.length ? config.columnas : defaultColumns(schema))
    : visibleCols;

  // Títulos de las páginas vinculadas (propiedades relation), desde la copia local
  const relationTitles = {};
  for (const it of notionItems) relationTitles[it.external_id] = it.title;

  const sinPropiedades = allPages.length > 0 && allPages.every(p => !p.properties);
  const activeFilters  = filterStatus.length + filterPriority.length + filterPeople.length;
  const resetFilters   = () => { setFilterStatus([]); setFilterPriority([]); setFilterPeople([]); setBusqueda(""); };
  const wsLetter       = (integration.workspace_name || "N")[0].toUpperCase();

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Barra superior ──────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap", flexShrink: 0 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#000", color: "#fff", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{wsLetter}</div>
        <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text)" }}>{integration.workspace_name || "Notion"}</span>
        <span style={{ color: "var(--text3)", fontSize: 12 }}>· Sincronizado {relativeTime(integration.last_synced_at || integration.connected_at)}</span>
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={() => setGestionando(true)} style={{ fontSize: 12.5, fontWeight: 600, padding: "6px 14px", borderRadius: 8, cursor: "pointer", border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)" }}>
          Elegir qué páginas mostrar
        </button>
        <button className="btn" onClick={handleSync} disabled={syncing} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, padding: "6px 14px", borderRadius: 8, cursor: syncing ? "not-allowed" : "pointer", border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", opacity: syncing ? 0.7 : 1 }}>
          <IconSync spinning={syncing} size={13} color="var(--text2)" />
          {syncing ? "Sincronizando…" : "Sincronizar"}
        </button>
      </div>

      {notionItems.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text3)", fontSize: 13 }}>
          No hay nada sincronizado. Pulsa Sincronizar, o elige qué páginas mostrar desde <b>Elegir qué páginas mostrar</b>.
        </div>
      ) : (
        <div className="nt-split" style={{ flex: 1, display: "flex", gap: 12, minHeight: 0 }}>
          {/* ── Navegador del workspace ──────────────────────────────────── */}
          <NotionNav
            items={notionItems}
            activeId={activeDbId}
            onSelect={nodo => {
              if (nodo.item_type === "database") { setNodoId(nodo.external_id); setSelectedPage(null); }
              else setSelectedPage(nodo);
            }} />

          {/* ── Contenido ────────────────────────────────────────────────── */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {!activeDb ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text3)", fontSize: 13 }}>
                Elige una base de datos en el árbol, o abre una página.
              </div>
            ) : (
              <>
                {/* Pestañas de vistas guardadas */}
                <div style={{ display: "flex", alignItems: "center", gap: 4, borderBottom: "1px solid var(--border)", overflowX: "auto", flexShrink: 0 }}>
                  {vistas.length === 0 && (
                    <VistaTab activa label="Vista general"
                      onClick={() => changeVista(null)}
                      onEdit={() => setEditando({ name: activeDb?.title || "Mi vista", config: semillaVista() })} />
                  )}
                  {vistas.map(v => (
                    <VistaTab key={v.id} activa={v.id === vistaId} label={v.name}
                      onClick={() => changeVista(v.id)}
                      onEdit={v.id === vistaId ? () => setEditando(v) : null} />
                  ))}
                </div>

                {/* Cabecera + filtros rápidos */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", flexWrap: "wrap", flexShrink: 0 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: "var(--bg)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                    {isEmoji(activeDb?.icon) ? activeDb.icon : <IconDatabase size={15} color="var(--text2)" />}
                  </div>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{activeDb?.title}</span>
                  <span style={{ color: "var(--text3)", fontSize: 12 }}>
                    {filtered.length !== allPages.length ? `${filtered.length} de ${allPages.length}` : allPages.length} páginas
                  </span>
                  {config?.filtros?.length > 0 && (
                    <span title={config.filtros.map(f => describeFiltro(f, schema)).join(config.modo === "alguno" ? "  ó  " : "  y  ")}
                      style={{ fontSize: 11, color: "var(--accent)", background: "var(--accent-bg)", border: "1px solid var(--accent)", borderRadius: 20, padding: "2px 9px", fontWeight: 600 }}>
                      {config.filtros.length} filtro{config.filtros.length === 1 ? "" : "s"} de la vista
                    </span>
                  )}
                  {modoEnlace && (
                    <span style={{ fontSize: 11, color: "var(--accent)", background: "var(--accent-bg)", border: "1px solid var(--accent)", borderRadius: 20, padding: "2px 9px", fontWeight: 600 }}>
                      Marca las que entran en ALM {todasDb ? "" : "· cargando…"}
                    </span>
                  )}
                  {modoSel && (
                    <span style={{ fontSize: 11, color: "var(--accent)", background: "var(--accent-bg)", border: "1px solid var(--accent)", borderRadius: 20, padding: "2px 9px", fontWeight: 600 }}>
                      Marca las que quieres en Mis tareas · solo tú las verás
                    </span>
                  )}
                  {arrastrable && (
                    <span title={`Arrastra una tarjeta para cambiar «${groupDef.name}» en Notion`}
                      style={{ fontSize: 11, color: "var(--text3)", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 20, padding: "2px 9px" }}>
                      <Icon name="pencil" size={10} color="var(--text3)"/> Editable
                    </span>
                  )}
                  <div style={{ flex: 1 }} />
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <button onClick={cambiarModoSel} aria-pressed={modoSel}
                      title="Elegir qué páginas de esta base de datos pasan a Mis tareas"
                      style={{
                        fontSize: 12, fontWeight: 600, padding: "5px 11px", borderRadius: 8, cursor: "pointer",
                        display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "var(--font)",
                        border: "1px solid " + (modoSel ? "var(--accent)" : "var(--border)"),
                        background: modoSel ? "var(--accent-bg)" : "var(--surface)",
                        color: modoSel ? "var(--accent)" : "var(--text2)",
                      }}>
                      <Icon name="check2" size={12} color="currentColor"/> Seleccionar
                    </button>
                    <button onClick={() => { setModoSel(false); setElegidas(new Set()); setModoEnlace(m => !m); }}
                      title="Marcar qué páginas de esta base de datos entran en ALM"
                      style={{
                        fontSize: 12, fontWeight: 600, padding: "5px 11px", borderRadius: 8, cursor: "pointer",
                        display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "var(--font)",
                        border: "1px solid " + (modoEnlace ? "var(--accent)" : "var(--border)"),
                        background: modoEnlace ? "var(--accent-bg)" : "var(--surface)",
                        color: modoEnlace ? "var(--accent)" : "var(--text2)",
                      }}>
                      {modoEnlace ? <><Icon name="check2" size={12} color="var(--accent)"/> Enlazando</> : "Enlazar"}
                    </button>
                    <input aria-label="Buscar en la vista" value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar…"
                      style={{ width: 130, padding: "5px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: 12, fontFamily: "var(--font)" }} />
                    {!config && <ViewSwitch mode={viewMode} onChange={changeView} />}
                    {!config && viewMode === "tabla" && schema.length > 0 && (
                      <MultiSelect label="Columnas" clearLabel="Quitar todas"
                        options={schema.filter(p => p.type !== "title").map(p => p.name)}
                        selected={visibleCols} onChange={changeCols} />
                    )}
                    {(statusOptions.length > 0 || priorityOptions.length > 0 || peopleOptions.length > 0) && (
                      <button onClick={() => setVerFiltros(v => !v)} aria-expanded={verFiltros || activeFilters > 0}
                        style={{ fontSize: 12, fontWeight: 600, padding: "5px 11px", borderRadius: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "var(--font)",
                          border: "1px solid " + (activeFilters > 0 ? "var(--accent)" : "var(--border)"), background: activeFilters > 0 ? "var(--accent-bg)" : "var(--surface)", color: activeFilters > 0 ? "var(--accent)" : "var(--text2)" }}>
                        <Icon name="funnel-fill" size={11} color="currentColor"/> Filtros{activeFilters > 0 ? ` (${activeFilters})` : ""}
                        <Icon name="chevron-down" size={10} color="currentColor"/>
                      </button>)}
                    {(verFiltros || activeFilters > 0) && <>
                    {statusOptions.length > 0   && <MultiSelect label="Estado"    options={statusOptions}   selected={filterStatus}   onChange={setFilterStatus} />}
                    {priorityOptions.length > 0 && <MultiSelect label="Prioridad" options={priorityOptions} selected={filterPriority} onChange={setFilterPriority} />}
                    {peopleOptions.length > 0   && <MultiSelect label="Asignados" options={peopleOptions}   selected={filterPeople}   onChange={setFilterPeople} ordenar buscarPlaceholder="Buscar persona..." sinCoincidencias="Nadie coincide" />}
                    </>}
                    {(activeFilters > 0 || busqueda) && <button onClick={resetFilters} style={{ fontSize: 11.5, color: "var(--text3)", cursor: "pointer", background: "transparent", border: "none", padding: "4px 6px", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="x" size={11} color="var(--text3)"/> Limpiar</button>}
                  </div>
                </div>

                {/* Contenido de la vista */}
                {sinPropiedades ? (
                  <div style={{ textAlign: "center", padding: "50px 20px", color: "var(--text3)", fontSize: 13 }}>
                    Pulsa <b>Sincronizar</b> para traer todas las propiedades de estas páginas.
                  </div>
                ) : filtered.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "50px 20px", color: "var(--text3)", fontSize: 13 }}>No hay páginas que coincidan con los filtros.</div>
                ) : tipo === "tabla" ? (
                  <NotionTable
                    pages={filtered}
                    schema={schema}
                    visibleCols={columnasTabla}
                    relationTitles={relationTitles}
                    onRowClick={setSelectedPage}
                    enlace={modoEnlace ? { onToggle: toggleEnlace, busy: enlaceBusy } : null}
                    seleccion={seleccion} />
                ) : !groupDef ? (
                  <div style={{ textAlign: "center", padding: "50px 20px", color: "var(--text3)", fontSize: 13 }}>
                    Esta vista es un tablero pero no tiene propiedad de agrupación. Edítala y elige por dónde agrupar.
                  </div>
                ) : (
                  <div style={{ flex: 1, overflowX: "auto", overflowY: "hidden", paddingBottom: 8 }}>
                    <div style={{ display: "flex", gap: 12, height: "100%", alignItems: "flex-start" }}>
                      {columnas.map(({ key, pages: cards }) => {
                        const dot       = statusDot(key).dot;
                        const droppable = canDropOn(key);
                        const isOver    = dragOver === key && droppable;
                        return (
                          <div key={key}
                            onDragOver={e => { if (droppable) { e.preventDefault(); setDragOver(key); } }}
                            onDragLeave={() => setDragOver(d => (d === key ? null : d))}
                            onDrop={e => handleDrop(e, key)}
                            style={{
                              width: 260, flexShrink: 0, display: "flex", flexDirection: "column", maxHeight: "100%",
                              borderRadius: 10, padding: 4, transition: "background 0.15s, outline 0.15s",
                              background: isOver ? "var(--accent-bg)" : "transparent",
                              outline: isOver ? "2px dashed var(--accent)" : "2px dashed transparent",
                            }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10, padding: "0 2px" }}>
                              <span style={{ width: 9, height: 9, borderRadius: "50%", background: dot, flexShrink: 0, display: "inline-block" }} />
                              <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>{key}</span>
                              <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text3)", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 20, padding: "0px 7px" }}>{cards.length}</span>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, overflowY: "auto", paddingBottom: 8, paddingRight: 2, minHeight: 40 }}>
                              {cards.map(page => {
                                const id = page.external_id ?? page.id;
                                return (
                                  <div key={id} style={{ opacity: busyIds.includes(id) ? 0.5 : 1 }}>
                                    <KanbanCard
                                      page={page}
                                      draggable={arrastrable}
                                      mover={arrastrable ? { columna: key, opciones: columnas.map(c => c.key).filter(canDropOn), onMover: moverA } : null}
                                      onToggleCompleted={toggleCompleted}
                                      enlace={modoEnlace ? { onToggle: toggleEnlace, busy: enlaceBusy } : null}
                                      seleccion={seleccion}
                                      onClick={() => setSelectedPage(page)} />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Barra de acciones del modo «Seleccionar» */}
                {modoSel && (
                  <div className="nt-barra" role="region" aria-label="Añadir a Mis tareas">
                    <span className="n">{elegidas.size} {elegidas.size === 1 ? "seleccionada" : "seleccionadas"}</span>
                    <button type="button" className="nt-b" disabled={!visiblesSinTraer.length || anadiendo}
                      onClick={() => setElegidas(new Set(visiblesSinTraer.map(idDe)))}>
                      <Icon name="check2" size={12} color="currentColor"/> Seleccionar visibles ({visiblesSinTraer.length})
                    </button>
                    <span className="sp" />
                    <button type="button" className="nt-b" disabled={!nFiltro || anadiendo}
                      onClick={() => (nFiltro > 50 ? setConfirmando(true) : anadirFiltro())}>
                      Añadir los {nFiltro} resultados del filtro
                    </button>
                    <button type="button" className="nt-b p" disabled={!elegidas.size || anadiendo}
                      onClick={() => anadir({ ids: [...elegidas] })}>
                      <Icon name="plus-lg" size={12} color="currentColor"/> Añadir a Mis tareas ({elegidas.size})
                    </button>
                    <button type="button" className="nt-b" onClick={cambiarModoSel}>Terminar</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Aviso para lectores de pantalla y confirmacion de muchas */}
      <div className="sr-only" role="status" aria-live="polite">{anuncio}</div>
      {anuncio && modoSel && (
        <div aria-hidden="true" style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: 78, background: "var(--text)", color: "var(--bg)", padding: "9px 13px", borderRadius: 9, fontSize: 12.5, zIndex: 250, maxWidth: "90vw" }}>{anuncio}</div>
      )}
      {confirmando && (
        <ConfirmarMuchas n={nFiltro} ocupado={anadiendo} onConfirmar={anadirFiltro} onCancelar={() => setConfirmando(false)} />
      )}

      {/* ── Panel de detalle ──────────────────────────────────────────────── */}
      {selectedPage && (
        <DetailPanel
          page={selectedPage}
          users={users}
          onClose={() => setSelectedPage(null)}
          onItemUpdated={item => { onItemUpdated?.(item); setSelectedPage(item); }}
          onItemRemoved={onItemRemoved}
          onNotify={notify} />
      )}

      {/* ── Editor de vistas ──────────────────────────────────────────────── */}
      {editando && (
        <NotionViewEditor
          vista={editando}
          schema={schema}
          ctx={{ users, pages: allPages, relationTitles }}
          onSave={guardarVista}
          onSaveAsNew={editando.id ? guardarComoNueva : null}
          onDelete={borrarVista}
          onClose={() => setEditando(null)} />
      )}

      {/* ── Páginas enlazadas ─────────────────────────────────────────────── */}
      {gestionando && (
        <NotionPagesManager
          token={token}
          onClose={() => setGestionando(false)}
          onChanged={onItemsChanged}
          onSync={handleSync}
          onNotify={notify} />
      )}
    </div>
  );
}

// Pestaña de una vista guardada. El lápiz solo sale en la vista activa: editar
// una vista que no estás viendo es la forma tonta de romper la que sí ves.
function VistaTab({ activa, label, onClick, onEdit }) {
  return (
    <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
      <button onClick={onClick} style={{
        display: "flex", alignItems: "center", gap: 6, padding: "9px 12px",
        fontSize: 13, fontWeight: activa ? 700 : 500, cursor: "pointer",
        border: "none", background: "transparent", fontFamily: "var(--font)",
        color: activa ? "var(--accent)" : "var(--text2)",
        borderBottom: activa ? "2px solid var(--accent)" : "2px solid transparent",
        marginBottom: "-1px", whiteSpace: "nowrap",
      }}>{label}</button>
      {onEdit && (
        <button onClick={onEdit} title="Configurar esta vista" aria-label="Configurar esta vista" style={{
          border: "none", background: "transparent", cursor: "pointer",
          color: activa ? "var(--accent)" : "var(--text3)",
          fontSize: 13, padding: "6px 8px 6px 0", marginBottom: "-1px", minWidth: 24, minHeight: 24,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}><Icon name="pencil" size={13} color={activa ? "var(--accent)" : "var(--text3)"}/></button>
      )}
    </div>
  );
}
